import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import DateTimePicker, { DateTimePickerEvent } from "@react-native-community/datetimepicker";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import modalStyles from "./CreateNotificationModal.style";
import { useToast } from "../../../components/LhToastContainer";
import { getAudienceFilterOptions, getAudienceCount } from "../../../api/audienceApi";
import { searchTickets, type TicketSearchResult } from "../../../api/ticketApi";
import { formatLocalDateTimeWithTimezone } from "../util/dateFormatter";
import { useTheme } from "../../../theme";

// ─── Types ────────────────────────────────────────────────────────────────────

type NotifType = "Scheduled Maintenance" | "Active Incident" | "General Announcement";
type Priority = "Urgent (auto-banner)" | "High" | "Standard" | "Low";
type AudienceType =
  | "All Users"
  | "By Region"
  | "By Market"
  | "By Location"
  | "By Role"
  | "By Target System";

interface FormState {
  notifType: NotifType;
  priority: Priority;
  title: string;
  message: string;
  linkedTicket: string;
  displayUntilClosed: boolean;
  includeActionButton: boolean;
  /** Multi-select audience dimensions. "All Users" overrides all others. */
  audienceTypes: AudienceType[];
  regions: string[];
  markets: string[];
  locations: string[];
  roles: string[];
  targetSystems: string[];
  sendVia: { inApp: boolean; email: boolean; teams: boolean };
  startDate: string;
  endDate: string;
  /** Live count returned by the audience/count API; stamped just before submit. */
  estimatedUserCount?: number;
}

interface FormErrors {
  title?: string;
  message?: string;
  audienceTypes?: string;
  startDate?: string;
  endDate?: string;
  dateRange?: string;
}

export type { FormState as CreateNotificationFormState };

const NOTIF_TYPES: NotifType[] = [
  "Scheduled Maintenance",
  "Active Incident",
  "General Announcement",
];
const PRIORITIES: Priority[] = ["Urgent (auto-banner)", "High", "Standard", "Low"];
const AUDIENCE_TYPES: AudienceType[] = [
  "All Users",
  "By Region",
  "By Market",
  "By Location",
  "By Role",
  "By Target System",
];
const ALL_LOCATIONS = [
  "Fullerton DC",
  "Bondurant DC",
  "Placentia DC",
  "Riverside DC (SAP: 1267)",
  "Ontario DC (SAP: 1278)",
];
const ALL_ROLES     = ["Sustain Team", "Field User", "Warehouse Manager", "System Admin", "IT Support"];
// ALL_SYSTEMS is now loaded from the API (executionSystems field); kept as static fallback only.
const ALL_SYSTEMS_FALLBACK = [
  "Centralized Purchasing", "EWM", "SAP",
  "Navigator", "Vision Pick (WES)", "Voice Pick",
];
const TITLE_MAX_LENGTH = 100;
const MESSAGE_MAX_LENGTH = 10000;
const ROLE_COUNTS: Record<string, number> = {
  "Sustain Team": 8, "Field User": 1950, "Warehouse Manager": 124, "System Admin": 23, "IT Support": 45,
};

/** Produces a datetime-local string ("YYYY-MM-DDTHH:mm") for the current moment. */
const nowIso = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Produces a datetime-local string offset by `hours` from now. */
const nowPlusHoursIso = (hours: number): string => {
  const d = new Date(Date.now() + hours * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Returns a fresh default form with start = now, end = now + 1 hour. */
const createDefaultForm = (): FormState => ({
  notifType: "Active Incident",
  priority: "Standard",
  title: "",
  message: "",
  linkedTicket: "",
  displayUntilClosed: false,
  includeActionButton: false,
  audienceTypes: ["By Location"],
  regions: [],
  markets: [],
  locations: [],
  roles: [],
  targetSystems: [],
  sendVia: { inApp: true, email: true, teams: false },
  startDate: nowIso(),
  endDate: nowPlusHoursIso(1),
});

// OR within dimension, AND across dimensions
const estimateRecipients = (f: FormState): number => {
  const types = Array.isArray(f.audienceTypes) ? f.audienceTypes : [];
  if (types.includes("All Users")) return 2847;
  const regions = Array.isArray(f.regions) ? f.regions : [];
  const markets = Array.isArray(f.markets) ? f.markets : [];
  const locations = Array.isArray(f.locations) ? f.locations : [];
  const roles = Array.isArray(f.roles) ? f.roles : [];
  const targetSystems = Array.isArray(f.targetSystems) ? f.targetSystems : [];
  // Sum across selected dimensions (estimate; AND between dims means real count is lower)
  let total = 0;
  if (types.includes("By Region"))        total += regions.length * 520;
  if (types.includes("By Market"))        total += markets.length * 210;
  if (types.includes("By Location"))      total += locations.length * 37;
  if (types.includes("By Role"))          total += roles.reduce((s, r) => s + (ROLE_COUNTS[r] ?? 30), 0);
  if (types.includes("By Target System")) total += targetSystems.length * 340;
  return total;
};

const validateForm = (f: FormState): FormErrors => {
  const errs: FormErrors = {};
  const title = f.title ?? "";
  const message = f.message ?? "";
  if (!title.trim())                           errs.title    = "Title is required.";
  else if (title.length > TITLE_MAX_LENGTH)     errs.title    = `Title must be ${TITLE_MAX_LENGTH} characters or less.`;
  if (!message.trim())                          errs.message  = "Message is required.";
  else if (message.length > MESSAGE_MAX_LENGTH) errs.message  = `Message must be ${MESSAGE_MAX_LENGTH.toLocaleString()} characters or less.`;
  const audienceTypes = Array.isArray(f.audienceTypes) ? f.audienceTypes : [];
  if (audienceTypes.length === 0) errs.audienceTypes = "Please select at least one audience type.";
  else if (!audienceTypes.includes("All Users")) {
    // Validate that each selected audience type has at least one value
    const missingValues: string[] = [];
    if (audienceTypes.includes("By Region") && (!f.regions || f.regions.length === 0)) missingValues.push("Region");
    if (audienceTypes.includes("By Market") && (!f.markets || f.markets.length === 0)) missingValues.push("Market");
    if (audienceTypes.includes("By Location") && (!f.locations || f.locations.length === 0)) missingValues.push("Location");
    if (audienceTypes.includes("By Role") && (!f.roles || f.roles.length === 0)) missingValues.push("Role");
    if (audienceTypes.includes("By Target System") && (!f.targetSystems || f.targetSystems.length === 0)) missingValues.push("System");
    
    if (missingValues.length > 0) {
      errs.audienceTypes = `Please select at least one value for: ${missingValues.join(", ")}.`;
    }
  }
  if (!f.displayUntilClosed) {
    if (!f.startDate)              errs.startDate = "Start date/time is required.";
    if (!f.endDate)                errs.endDate   = "End date/time is required.";
    else if (f.startDate && new Date(f.endDate) <= new Date(f.startDate))
      errs.dateRange = "End date must be after start date.";
  }
  return errs;
};

// ─── Dark-mode overrides (native iPad / mobile) ──────────────────────────────
// Applied AFTER the light `modalStyles` so light mode is untouched. All values
// come from the global theme tokens (no hardcoded colors).

const useDarkOverrides = () => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const dk = useMemo(() => {
    if (!isDark) return null;
    const field = {
      backgroundColor: colors.background.elevated,
      borderColor: colors.input.border,
    };
    return {
      label: { color: colors.text.secondary },
      muted: { color: colors.text.disabled },
      body: { color: colors.text.primary },
      field,
      fieldPressed: { backgroundColor: colors.border.divider },
      fieldOpen: { backgroundColor: colors.background.elevated, borderColor: colors.brand.primary },
      menu: { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
      menuItemActive: { backgroundColor: colors.statusBackground.info },
      menuItemPressed: { backgroundColor: colors.border.divider },
      menuItemTextActive: { color: colors.brand.primary },
      checkbox: { backgroundColor: colors.background.elevated, borderColor: colors.input.border },
      checkboxChecked: { backgroundColor: colors.brand.primary, borderColor: colors.brand.primary },
      divider: { borderBottomColor: colors.border.divider },
      panel: { backgroundColor: colors.background.elevated, borderColor: colors.border.divider },
      infoNote: { backgroundColor: colors.statusBackground.info },
      brandText: { color: colors.brand.primary },
      secondaryText: { color: colors.text.secondary },
      toggleTrack: { backgroundColor: colors.input.border },
      toggleTrackOn: { backgroundColor: colors.brand.primary },
      toggleLabelDisabled: { color: colors.text.disabled },
      chip: { backgroundColor: colors.background.elevated, borderColor: colors.input.border },
      chipSelected: { backgroundColor: colors.statusBackground.info, borderColor: colors.brand.primary },
      chipDisabled: { backgroundColor: colors.background.surface, borderColor: colors.border.divider },
      chipPressed: { backgroundColor: colors.border.divider },
      chipTextDisabled: { color: colors.text.disabled },
    };
  }, [isDark, colors]);
  return { isDark, colors, dk };
};

// ─── Tiny inline select dropdown ─────────────────────────────────────────────

interface SelectProps<T extends string> {
  label: string;
  required?: boolean;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
}

function InlineSelect<T extends string>({
  label,
  required,
  value,
  options,
  onChange,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<any>(null);
  const { isDark, colors, dk } = useDarkOverrides();

  const handleOpen = () => {
    if (open) { setOpen(false); return; }
    triggerRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setDropPos({ top: y + h + 2, left: x, width: w });
      setOpen(true);
    });
  };

  return (
    <View style={modalStyles.fieldWrap}>
      <Text style={[modalStyles.label, dk?.label]}>
        {label}
        {required ? <Text style={modalStyles.req}> *</Text> : null}
      </Text>
      {/* Trigger */}
      <Pressable
        ref={triggerRef}
        onPress={handleOpen}
        style={({ pressed }) => [
          modalStyles.selectTrigger,
          dk?.field,
          open ? modalStyles.selectTriggerOpen : null,
          open ? dk?.fieldOpen : null,
          pressed ? modalStyles.selectTriggerPressed : null,
          pressed ? dk?.fieldPressed : null,
        ]}
      >
        <Text style={[modalStyles.selectText, dk?.body]} numberOfLines={1}>{value}</Text>
        <MaterialIcons
          name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"}
          size={18}
          color={open ? (isDark ? colors.brand.primary : "#034895") : (isDark ? colors.text.secondary : "#585858")}
        />
      </Pressable>
      {/* Popup overlay menu */}
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={popupStyles.backdrop} onPress={() => setOpen(false)} />
        <View style={[popupStyles.dropdown, dk?.menu, { top: dropPos.top, left: dropPos.left, minWidth: dropPos.width }]}>
          <ScrollView bounces={false} showsVerticalScrollIndicator keyboardShouldPersistTaps="handled" style={{ maxHeight: 260 }}>
            {options.map((opt) => (
              <Pressable
                key={opt}
                onPress={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                style={({ pressed }) => [
                  modalStyles.selectMenuItem,
                  opt === value ? modalStyles.selectMenuItemActive : null,
                  opt === value ? dk?.menuItemActive : null,
                  pressed ? modalStyles.selectMenuItemPressed : null,
                  pressed ? dk?.menuItemPressed : null,
                ]}
              >
                <Text
                  style={[
                    modalStyles.selectMenuItemText,
                    dk?.body,
                    opt === value ? modalStyles.selectMenuItemTextActive : null,
                    opt === value ? dk?.menuItemTextActive : null,
                  ]}
                >
                  {opt}
                </Text>
                {opt === value && (
                  <MaterialIcons name="check" size={14} color={isDark ? colors.brand.primary : "#034895"} />
                )}
              </Pressable>
            ))}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
}

// ─── Multi-select dropdown ────────────────────────────────────────────────────

interface MultiSelectProps {
  label: string;
  values: string[];
  options: string[];
  onToggle: (v: string) => void;
  helpText?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  values,
  options,
  onToggle,
  helpText,
}) => {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<any>(null);

  const displayText =
    values.length === 0
      ? "Select locations…"
      : values.length === options.length
      ? "All locations"
      : values.length === 1
      ? values[0]
      : `${values.length} locations selected`;

  const handleOpen = () => {
    if (open) { setOpen(false); return; }
    triggerRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setDropPos({ top: y + h + 2, left: x, width: w });
      setOpen(true);
    });
  };

  return (
    <View style={modalStyles.fieldWrap}>
      <Text style={modalStyles.label}>{label}</Text>
      {/* Trigger */}
      <Pressable
        ref={triggerRef}
        onPress={handleOpen}
        style={({ pressed }) => [
          modalStyles.selectTrigger,
          open ? modalStyles.selectTriggerOpen : null,
          pressed ? modalStyles.selectTriggerPressed : null,
        ]}
      >
        <Text style={modalStyles.selectText} numberOfLines={1}>{displayText}</Text>
        <MaterialIcons
          name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"}
          size={18}
          color={open ? "#034895" : "#585858"}
        />
      </Pressable>
      {/* Popup overlay menu */}
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={popupStyles.backdrop} onPress={() => setOpen(false)} />
        <View style={[popupStyles.dropdown, { top: dropPos.top, left: dropPos.left, minWidth: dropPos.width }]}>
          <ScrollView style={{ maxHeight: 260 }} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator>
            {options.map((opt) => {
              const checked = values.includes(opt);
              return (
                <Pressable
                  key={opt}
                  onPress={() => onToggle(opt)}
                  style={({ pressed }) => [
                    modalStyles.selectMenuItem,
                    checked ? modalStyles.selectMenuItemActive : null,
                    pressed ? modalStyles.selectMenuItemPressed : null,
                  ]}
                >
                  <View style={[modalStyles.checkbox, checked ? modalStyles.checkboxChecked : null]}>
                    {checked && <MaterialIcons name="check" size={10} color="#ffffff" />}
                  </View>
                  <Text
                    style={[
                      modalStyles.selectMenuItemText,
                      { marginLeft: 8 },
                      checked ? modalStyles.selectMenuItemTextActive : null,
                    ]}
                  >
                    {opt}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </Modal>
      {helpText ? <Text style={modalStyles.helpText}>{helpText}</Text> : null}
    </View>
  );
}

// ─── Audience multi-select (API-driven, searchable) ───────────────────────────

interface AudienceMultiSelectProps {
  label: string;
  entityName: "Region" | "Market" | "Location" | "Role" | "System";
  values: string[];
  options: { value: string; label: string }[];
  onToggle: (id: string) => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  helpText?: string;
}

const AudienceMultiSelect: React.FC<AudienceMultiSelectProps> = ({
  label, entityName, values, options, onToggle, loading, error, onRetry, helpText,
}) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<any>(null);
  const { isDark, colors, dk } = useDarkOverrides();

  const filtered = useMemo(
    () => search.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : options,
    [options, search],
  );

  const isDisabled = loading || !!error;

  const displayText = loading
    ? "Loading\u2026"
    : error
    ? "Unable to load"
    : values.length === 0
    ? `Select ${entityName}s\u2026`
    : values.length === options.length && options.length > 0
    ? `All ${entityName}s`
    : `${values.length} ${entityName}${values.length !== 1 ? "s" : ""} selected`;

  const handleOpen = () => {
    if (isDisabled) return;
    if (open) { setOpen(false); return; }
    triggerRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setDropPos({ top: y + h + 2, left: x, width: w });
      setOpen(true);
    });
  };

  return (
    <View style={modalStyles.fieldWrap}>
      <Text style={[modalStyles.label, dk?.label]}>{label}</Text>

      {/* Trigger */}
      <Pressable
        ref={triggerRef}
        onPress={handleOpen}
        style={({ pressed }) => [
          modalStyles.selectTrigger,
          dk?.field,
          open && !isDisabled ? modalStyles.selectTriggerOpen : null,
          open && !isDisabled ? dk?.fieldOpen : null,
          pressed && !isDisabled ? modalStyles.selectTriggerPressed : null,
          pressed && !isDisabled ? dk?.fieldPressed : null,
          isDisabled ? ({ opacity: 0.65 } as any) : null,
        ]}
      >
        {loading ? (
          <ActivityIndicator size="small" color={isDark ? colors.brand.primary : "#034895"} style={{ marginRight: 6 }} />
        ) : null}
        <Text
          style={[modalStyles.selectText, dk?.body, error ? { color: isDark ? colors.status.error : "#C12C01" } : null]}
          numberOfLines={1}
        >
          {displayText}
        </Text>
        {!loading ? (
          <MaterialIcons
            name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"}
            size={18}
            color={open ? (isDark ? colors.brand.primary : "#034895") : (isDark ? colors.text.secondary : "#585858")}
          />
        ) : null}
      </Pressable>

      {/* Error + Retry */}
      {error ? (
        <View style={[modalStyles.errorRow, { marginTop: 6 }]}>
          <MaterialIcons name="error-outline" size={12} color="#C12C01" />
          <Text style={[modalStyles.errorText, { flex: 1 }]}>{error}</Text>
          <Pressable onPress={onRetry} style={{ marginLeft: 8 }}>
            <Text style={{ fontSize: 11, color: isDark ? colors.brand.primary : "#034895", textDecorationLine: "underline" }}>
              Retry
            </Text>
          </Pressable>
        </View>
      ) : null}

      {/* Popup overlay menu with search */}
      <Modal visible={open && !isDisabled} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={popupStyles.backdrop} onPress={() => setOpen(false)} />
        <View style={[popupStyles.dropdown, dk?.menu, { top: dropPos.top, left: dropPos.left, minWidth: dropPos.width }]}>
          {/* Search bar */}
          <View style={[modalStyles.ticketSearchWrap, dk?.field, { margin: 8, marginBottom: 4 }]}>
            <MaterialIcons name="search" size={14} color={isDark ? colors.text.secondary : "#818181"} />
            <TextInput
              style={[modalStyles.ticketSearchInput, dk?.body]}
              placeholder={`Search ${entityName.toLowerCase()}s\u2026`}
              placeholderTextColor={isDark ? colors.text.disabled : "#a8a8a8"}
              value={search}
              onChangeText={setSearch}
            />
            {search.length > 0 ? (
              <Pressable onPress={() => setSearch("")} style={{ padding: 2 }}>
                <MaterialIcons name="close" size={13} color={isDark ? colors.text.secondary : "#818181"} />
              </Pressable>
            ) : null}
          </View>

          {/* Scrollable items list */}
          <ScrollView
            style={{ maxHeight: 220 }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            nestedScrollEnabled
          >
            {filtered.length === 0 ? (
              <Text style={{ paddingHorizontal: 12, paddingVertical: 10, fontSize: 12, color: isDark ? colors.text.secondary : "#818181", textAlign: "center" }}>
                No {entityName.toLowerCase()}s found.
              </Text>
            ) : filtered.map((opt) => {
              const checked = values.includes(opt.value);
              return (
                <Pressable
                  key={opt.value}
                  onPress={() => onToggle(opt.value)}
                  style={({ pressed }) => [
                    modalStyles.selectMenuItem,
                    checked ? modalStyles.selectMenuItemActive : null,
                    checked ? dk?.menuItemActive : null,
                    pressed ? modalStyles.selectMenuItemPressed : null,
                    pressed ? dk?.menuItemPressed : null,
                  ]}
                >
                  <View style={[modalStyles.checkbox, dk?.checkbox, checked ? modalStyles.checkboxChecked : null, checked ? dk?.checkboxChecked : null]}>
                    {checked ? <MaterialIcons name="check" size={10} color="#ffffff" /> : null}
                  </View>
                  <Text
                    style={[
                      modalStyles.selectMenuItemText,
                      dk?.body,
                      { marginLeft: 8 },
                      checked ? modalStyles.selectMenuItemTextActive : null,
                      checked ? dk?.menuItemTextActive : null,
                    ]}
                  >
                    {opt.label}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </Modal>

      {helpText ? <Text style={[modalStyles.helpText, dk?.muted]}>{helpText}</Text> : null}
    </View>
  );
};

// ─── Date/Time field ─────────────────────────────────────────────────────────

interface DateTimeFieldProps {
  label: string;
  value: string; // "YYYY-MM-DDTHH:mm"
  onChange: (v: string) => void;
}

const isoToDate = (iso: string): Date => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? new Date() : d;
};

const dateToIso = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDisplay = (iso: string): string => {
  const d = isoToDate(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}  ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DateTimeField: React.FC<DateTimeFieldProps> = ({ label, value, onChange }) => {
  // Android needs two-step: date first, then time
  const [androidMode, setAndroidMode] = useState<"date" | "time" | null>(null);
  // iOS/Android: show the picker; on web we use the native browser input
  const [showIos, setShowIos] = useState(false);
  const { isDark, colors, dk } = useDarkOverrides();

  const date = isoToDate(value);

  const handleNativeChange = (_event: DateTimePickerEvent, selected?: Date) => {
    if (!selected) {
      setAndroidMode(null);
      setShowIos(false);
      return;
    }
    if (Platform.OS === "android") {
      if (androidMode === "date") {
        // Preserve existing time on the newly picked date
        const next = new Date(selected);
        next.setHours(date.getHours(), date.getMinutes());
        onChange(dateToIso(next));
        // Now show time picker
        setAndroidMode("time");
      } else {
        // Merge the time into the already-updated date
        const base = isoToDate(value);
        base.setHours(selected.getHours(), selected.getMinutes());
        onChange(dateToIso(base));
        setAndroidMode(null);
      }
    } else {
      // iOS spinner: update continuously
      onChange(dateToIso(selected));
    }
  };

  // ── Web ──────────────────────────────────────────────────────────────────
  if (Platform.OS === "web") {
    // React.createElement with a string tag renders a true HTML element in RN Web.
    // This is the only reliable way to get onChange(e.target.value) for
    // datetime-local inputs — RN TextInput's onChangeText does NOT fire for them.
    return (
      <View style={modalStyles.dateFieldWrap}>
        {label ? <Text style={modalStyles.dateFieldLabel}>{label}</Text> : null}
        {React.createElement("input", {
          type: "datetime-local",
          value: value,
          onChange: (e: any) => onChange(e.target.value),
          style: {
            border: "1px solid #D0D0D0",
            borderRadius: 8,
            padding: "9px 10px",
            fontSize: 13,
            color: "#2d2d2d",
            backgroundColor: "#fafafa",
            fontFamily: "Inter, sans-serif",
            fontWeight: "400",
            width: "100%",
            boxSizing: "border-box",
            cursor: "pointer",
            outline: "none",
          },
        })}
        <Text style={{ fontSize: 11, color: "#818181", marginTop: 5 }}>
          {formatLocalDateTimeWithTimezone(value)}
        </Text>
      </View>
    );
  }

  // ── iOS ───────────────────────────────────────────────────────────────────
  if (Platform.OS === "ios") {
    return (
      <View style={modalStyles.dateFieldWrap}>
        <Text style={[modalStyles.dateFieldLabel, dk?.muted]}>{label}</Text>
        <Pressable
          onPress={() => setShowIos((p) => !p)}
          style={({ pressed }) => [
            modalStyles.dateTrigger,
            dk?.field,
            pressed ? modalStyles.dateTriggerPressed : null,
            pressed ? dk?.fieldPressed : null,
            showIos ? modalStyles.dateTriggerOpen : null,
            showIos ? dk?.fieldOpen : null,
          ]}
        >
          <Text style={[modalStyles.dateTriggerText, dk?.body]}>{formatDisplay(value)}</Text>
          <MaterialIcons
            name="calendar-today"
            size={15}
            color={showIos ? (isDark ? colors.brand.primary : "#034895") : (isDark ? colors.text.secondary : "#585858")}
          />
        </Pressable>
        <Text style={[{ fontSize: 11, color: "#818181", marginTop: 5 }, dk?.muted]}>
          {formatLocalDateTimeWithTimezone(value)}
        </Text>
        {showIos && (
          <DateTimePicker
            value={date}
            mode="datetime"
            display="spinner"
            onChange={handleNativeChange}
            style={{ marginTop: 4 }}
            {...(isDark ? { themeVariant: "dark" as const, textColor: colors.text.primary } : null)}
          />
        )}
      </View>
    );
  }

  // ── Android ───────────────────────────────────────────────────────────────
  return (
    <View style={modalStyles.dateFieldWrap}>
      <Text style={[modalStyles.dateFieldLabel, dk?.muted]}>{label}</Text>
      <Pressable
        onPress={() => setAndroidMode("date")}
        style={({ pressed }) => [
          modalStyles.dateTrigger,
          dk?.field,
          pressed ? modalStyles.dateTriggerPressed : null,
          pressed ? dk?.fieldPressed : null,
        ]}
      >
        <Text style={[modalStyles.dateTriggerText, dk?.body]}>{formatDisplay(value)}</Text>
        <MaterialIcons name="calendar-today" size={15} color={isDark ? colors.text.secondary : "#585858"} />
      </Pressable>
      <Text style={[{ fontSize: 11, color: "#818181", marginTop: 5 }, dk?.muted]}>
        {formatLocalDateTimeWithTimezone(value)}
      </Text>
      {androidMode !== null && (
        <DateTimePicker
          value={date}
          mode={androidMode}
          display="default"
          onChange={handleNativeChange}
        />
      )}
    </View>
  );
};

// ─── Field error ──────────────────────────────────────────────────────────────

const FieldError: React.FC<{ error?: string }> = ({ error }) =>
  error ? (
    <View style={modalStyles.errorRow}>
      <MaterialIcons name="error-outline" size={12} color="#C12C01" />
      <Text style={modalStyles.errorText}>{error}</Text>
    </View>
  ) : null;

// ─── Toggle switch ────────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  label: string;
  sublabel?: string;
  value: boolean;
  onToggle: () => void;
  disabled?: boolean;
  disabledHint?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  label, sublabel, value, onToggle, disabled, disabledHint,
}) => {
  const { dk } = useDarkOverrides();
  const on = value && !disabled;
  return (
    <Pressable
      onPress={disabled ? undefined : onToggle}
      style={[modalStyles.toggleRow, disabled ? modalStyles.toggleRowDisabled : null]}
    >
      <View style={[modalStyles.toggleTrack, dk?.toggleTrack, on ? modalStyles.toggleTrackOn : null, on ? dk?.toggleTrackOn : null]}>
        <View style={[modalStyles.toggleThumb, on ? modalStyles.toggleThumbOn : null]} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={[modalStyles.toggleLabel, dk?.body, disabled ? modalStyles.toggleLabelDisabled : null, disabled ? dk?.toggleLabelDisabled : null]}>
          {label}
        </Text>
        {(disabled && disabledHint) || sublabel ? (
          <Text style={[modalStyles.toggleHint, dk?.muted]}>{disabled ? disabledHint : sublabel}</Text>
        ) : null}
      </View>
    </Pressable>
  );
};

// ─── Ticket search ────────────────────────────────────────────────────────────

interface TicketSearchProps {
  value: string;
  onChange: (id: string) => void;
}

const TicketSearch: React.FC<TicketSearchProps> = ({ value, onChange }) => {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<TicketSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isDark, colors, dk } = useDarkOverrides();

  useEffect(() => { setQuery(value); }, [value]);

  // Debounced search API call
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    const trimmedQuery = query.trim();
    
    // Empty query — show no results
    if (!trimmedQuery) {
      setResults([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const tickets = await searchTickets({
          search: trimmedQuery,
          page: 0,
          size: 10,
          excludeClosedResolved: true,
          excludeMerged: true,
          sortBy: "createdAt",
          sortOrder: "DESC",
        });
        setResults(tickets);
      } catch (err) {
        console.error("Ticket search error:", err);
        setError("Failed to search tickets");
      }
      setLoading(false);
    }, 400);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [query]);

  const handleSelect = (ticketNumber: string) => { 
    setQuery(ticketNumber); 
    onChange(ticketNumber); 
    setOpen(false); 
  };

  // On web, mousedown fires before blur; preventing default keeps the TextInput
  // focused so onPress can fire normally on mouseup.
  const dropdownMouseDownProps = Platform.OS === "web"
    ? { onMouseDown: (e: any) => e.preventDefault() }
    : {};

  return (
    <View>
      <View style={[modalStyles.ticketSearchWrap, dk?.field]}>
        <MaterialIcons name="search" size={15} color={isDark ? colors.text.secondary : "#818181"} />
        <TextInput
          style={[modalStyles.ticketSearchInput, dk?.body]}
          placeholder="Search by ticket ID or title…"
          placeholderTextColor={isDark ? colors.text.disabled : "#a8a8a8"}
          value={query}
          onChangeText={(v) => { 
            setQuery(v); 
            if (v.trim().length === 0) {
              onChange(""); 
            }
            setOpen(true); 
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
        {loading && (
          <ActivityIndicator size="small" color={isDark ? colors.brand.primary : "#034895"} style={{ marginRight: 8 }} />
        )}
        {query.length > 0 && !loading && (
          <Pressable 
            onPress={() => { 
              setQuery(""); 
              onChange(""); 
              setResults([]);
              setError(null);
            }}
            style={{ padding: 4 }}
          >
            <MaterialIcons name="close" size={14} color={isDark ? colors.text.secondary : "#818181"} />
          </Pressable>
        )}
      </View>
      {open && (
        <View style={[modalStyles.selectMenu, dk?.menu]} {...dropdownMouseDownProps}>
          {error ? (
            <View style={{ padding: 12, alignItems: "center" }}>
              <MaterialIcons name="error-outline" size={14} color="#C12C01" />
              <Text style={{ fontSize: 12, color: "#C12C01", marginTop: 6, textAlign: "center" }}>
                {error}
              </Text>
            </View>
          ) : results.length === 0 && !loading && query.trim().length > 0 ? (
            <View style={{ padding: 12, alignItems: "center" }}>
              <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : "#818181" }}>No tickets found</Text>
            </View>
          ) : (
            results.map((t) => (
              <Pressable
                key={t.ticketId}
                onPress={() => handleSelect(t.ticketNumber)}
                style={({ pressed }) => [
                  modalStyles.ticketOption, 
                  dk?.divider,
                  pressed ? modalStyles.selectMenuItemPressed : null,
                  pressed ? dk?.menuItemPressed : null,
                ]}
              >
                <Text style={[modalStyles.ticketOptionId, dk?.brandText]}>{t.ticketNumber}</Text>
                <Text 
                  style={[modalStyles.ticketOptionTitle, dk?.secondaryText]} 
                  numberOfLines={1}
                >
                  {t.title}
                </Text>
              </Pressable>
            ))
          )}
        </View>
      )}
    </View>
  );
};

// ─── Checkbox row ─────────────────────────────────────────────────────────────

interface CheckboxProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
}

const CheckRow: React.FC<CheckboxProps> = ({ label, checked, onToggle }) => {
  const { dk } = useDarkOverrides();
  return (
    <Pressable onPress={onToggle} style={modalStyles.checkRow}>
      <View style={[modalStyles.checkbox, dk?.checkbox, checked ? modalStyles.checkboxChecked : null, checked ? dk?.checkboxChecked : null]}>
        {checked && <MaterialIcons name="check" size={11} color="#ffffff" />}
      </View>
      <Text style={[modalStyles.checkLabel, dk?.body]}>{label}</Text>
    </Pressable>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface Props {
  visible: boolean;
  onClose: () => void;
  onDraft: (formData: FormState) => void;
  onSend: (formData: FormState) => void | Promise<void>;
  /** When provided the modal opens in edit mode, pre-filled with these values */
  initialValues?: FormState;
  isDark?: boolean;
  colors?: any;
}

const CreateNotificationModal: React.FC<Props> = ({
  visible,
  onClose,
  onDraft,
  onSend,
  initialValues,
  isDark,
  colors,
}) => {
  const toast = useToast();
  const { dk, colors: themeColors } = useDarkOverrides();
  const isEditMode = !!initialValues;
  const [form, setForm]         = useState<FormState>(() => createDefaultForm());
  const [errors, setErrors]     = useState<FormErrors>({});
  const [titleFocused, setTitleFocused] = useState(false);
  const [msgFocused,   setMsgFocused]   = useState(false);
  const [msgOverflowWarning, setMsgOverflowWarning] = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [sendLoading,  setSendLoading]  = useState(false);

  // Reset form whenever the modal opens — fill from initialValues (edit) or a fresh default (create)
  useEffect(() => {
    if (!visible) return;
    if (initialValues) {
      // Merge initialValues with defaults to ensure all audience arrays are initialized
      const mergedForm: FormState = {
        ...createDefaultForm(),
        ...initialValues,
        // Ensure audienceTypes is always an array
        audienceTypes: Array.isArray(initialValues.audienceTypes) && initialValues.audienceTypes.length > 0
          ? initialValues.audienceTypes
          : ["All Users"],
        // Explicitly ensure all audience arrays are arrays, never undefined
        regions: Array.isArray(initialValues.regions) ? initialValues.regions : [],
        markets: Array.isArray(initialValues.markets) ? initialValues.markets : [],
        locations: Array.isArray(initialValues.locations) ? initialValues.locations : [],
        roles: Array.isArray(initialValues.roles) ? initialValues.roles : [],
        targetSystems: Array.isArray(initialValues.targetSystems) ? initialValues.targetSystems : [],
      };
      setForm(mergedForm);
    } else {
      setForm(createDefaultForm());
    }
    setErrors({});
    setShowConfirm(false);
    setTitleFocused(false);
    setMsgFocused(false);
    setMsgOverflowWarning(false);
    setSendLoading(false);
  }, [visible, initialValues]);

  // ── Audience filter options — single API fetch ─────────────────────────────
  type DimOptions = { value: string; label: string }[];
  const [allOptions, setAllOptions] = useState<{
    regions: DimOptions;
    markets: DimOptions;
    locations: DimOptions;
    roles: DimOptions;
    systems: DimOptions;
  }>({
    regions: [], markets: [], locations: [], roles: [],
    systems: ALL_SYSTEMS_FALLBACK.map((s) => ({ value: s, label: s })),
  });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError,   setOptionsError]   = useState<string | null>(null);
  const [optionsFetchKey, setOptionsFetchKey] = useState(0);

  // Fetch once when the modal opens (or on retry).
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError(null);
    getAudienceFilterOptions()
      .then((d) => {
        if (cancelled) return;
        // Filter out empty/blank values, remove duplicates, and sort
        const toOpts = (arr: string[]) => {
          const unique = [...new Set(arr.filter(v => v && v.trim()))];
          return unique.sort().map((v) => ({ value: v, label: v }));
        };
        // Special handling for roles: filter out "Site Supervisorr" (typo with double 'r')
        const toOptsRoles = (arr: string[]) => {
          const filtered = arr.filter(v => v && v.trim() && v !== "Site Supervisorr");
          const unique = [...new Set(filtered)];
          return unique.sort().map((v) => ({ value: v, label: v }));
        };
        setAllOptions({
          regions:   toOpts(d.regions),
          markets:   toOpts(d.markets),
          locations: toOpts(d.locations),
          roles:     toOptsRoles(d.roles),
          systems:   toOpts(d.executionSystems.length > 0 ? d.executionSystems : ALL_SYSTEMS_FALLBACK),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setOptionsError("Unable to load audience options. Please retry.");
      })
      .finally(() => { if (!cancelled) setOptionsLoading(false); });
    return () => { cancelled = true; };
  // optionsFetchKey increments on manual retry
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, optionsFetchKey]);

  // Helper: build per-dimension loading/error props for AudienceMultiSelect
  const dimProps = (key: keyof typeof allOptions) => ({
    options: allOptions[key],
    loading: optionsLoading,
    error:   optionsError,
    onRetry: () => setOptionsFetchKey((k) => k + 1),
  });

  const [audienceFetchKey] = useState(0); // kept for legacy compat — no longer used
  const fetchingRef = useRef<Set<string>>(new Set()); // kept for legacy compat

  // ── Live recipient count via API ───────────────────────────────────
  const [recipientCount,    setRecipientCount]    = useState<number | null>(null);
  const [recipientLoading,  setRecipientLoading]  = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const types: string[] = Array.isArray(form.audienceTypes) ? form.audienceTypes : [];

    // Build the count payload from currently selected dimensions
    const payload: Record<string, string[]> = {};
    
    if (types.includes("All Users")) {
      // All Users: call API with empty payload to get total count
    } else if (types.length > 0) {
      // Specific dimensions selected: build payload from selected values
      if (types.includes("By Region")        && form.regions.length > 0)       payload.regions          = form.regions;
      if (types.includes("By Market")        && form.markets.length > 0)       payload.markets          = form.markets;
      if (types.includes("By Location")      && form.locations.length > 0)     payload.locations        = form.locations;
      if (types.includes("By Role")          && form.roles.length > 0)         payload.roles            = form.roles;
      if (types.includes("By Target System") && form.targetSystems.length > 0) payload.executionSystems = form.targetSystems;

      // No dimensions have values yet — clear the count
      if (Object.keys(payload).length === 0) {
        setRecipientCount(0);
        setRecipientLoading(false);
        return;
      }
    } else {
      // No audience type selected — clear the count
      setRecipientCount(0);
      setRecipientLoading(false);
      return;
    }

    // Debounce to avoid an API call on every keystroke/toggle
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setRecipientLoading(true);
    debounceRef.current = setTimeout(() => {
      let cancelled = false;
      getAudienceCount(payload)
        .then((n) => { if (!cancelled) { setRecipientCount(n); setRecipientLoading(false); } })
        .catch(() => { if (!cancelled) { setRecipientCount(null); setRecipientLoading(false); } });
      return () => { cancelled = true; };
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.audienceTypes, form.regions, form.markets, form.locations, form.roles, form.targetSystems]);

  const patch = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  // Special handler for date changes with real-time validation
  const patchDateWithValidation = (key: "startDate" | "endDate", val: string) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: val };
      // Check if both dates are set and end date is before or equal to start date
      if (updated.startDate && updated.endDate && new Date(updated.endDate) <= new Date(updated.startDate)) {
        setErrors((e) => ({ ...e, dateRange: "End date must be after start date." }));
      } else {
        // Clear date-related errors if validation passes
        setErrors((e) => {
          const { startDate, endDate, dateRange, ...rest } = e;
          return rest;
        });
      }
      return updated;
    });
  };

  // Special handler for linkedTicket: auto-reset dependent fields when cleared
  const patchLinkedTicket = (ticketId: string) => {
    setForm((prev) => {
      // If clearing the ticket, also reset the dependent flags
      if (!ticketId) {
        return {
          ...prev,
          linkedTicket: ticketId,
          includeActionButton: false,
          displayUntilClosed: false,
        };
      }
      // If setting a ticket, just update it (keep existing flags)
      return { ...prev, linkedTicket: ticketId };
    });
  };

  const toggleArray = (
    key: "regions" | "markets" | "locations" | "roles" | "targetSystems",
    val: string
  ) => setForm((prev) => {
    const arr = Array.isArray(prev[key]) ? prev[key] : [];
    return {
      ...prev,
      [key]: arr.includes(val) ? arr.filter((v: string) => v !== val) : [...arr, val],
    };
  });

  // Clear previous audience selections and switch audience type
  const handleAudienceTypeToggle = (type: AudienceType) => {
    setForm((prev) => {
      const current = Array.isArray(prev.audienceTypes) ? prev.audienceTypes : [];
      if (type === "All Users") {
        // Toggle All Users — when turning on, clear all specific selections
        if (current.includes("All Users")) {
          return { ...prev, audienceTypes: [] };
        }
        return {
          ...prev,
          audienceTypes: ["All Users"],
          regions: [], markets: [], locations: [], roles: [], targetSystems: [],
        };
      }
      // Remove "All Users" override when selecting a specific dimension
      const withoutAll = current.filter((t) => t !== "All Users");
      if (withoutAll.includes(type)) {
        // Deselect: remove this type and clear its values
        const next = withoutAll.filter((t) => t !== type);
        const updates: Partial<FormState> = {};
        if (type === "By Region")        updates.regions = [];
        if (type === "By Market")        updates.markets = [];
        if (type === "By Location")      updates.locations = [];
        if (type === "By Role")          updates.roles = [];
        if (type === "By Target System") updates.targetSystems = [];
        return { ...prev, audienceTypes: next, ...updates };
      }
      return { ...prev, audienceTypes: [...withoutAll, type] };
    });
  };

  const isDirty = (form.title?.trim() ?? "") !== "" || (form.message?.trim() ?? "") !== "";

  const resetForm = () => { setForm(createDefaultForm()); setErrors({}); setMsgOverflowWarning(false); };

  const handleClose = () => {
    if (isDirty) {
      setShowConfirm(true);
    } else {
      resetForm();
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirm(false);
    resetForm();
    onClose();
  };

  const handleDraft = () => {
    if (!(form.title ?? "").trim()) {
      setErrors({ title: "Please add a title before saving as draft." });
      return;
    }
    const snapshot = { ...form };
    resetForm();
    onDraft(snapshot);
  };

  const handleSend = async () => {
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.show("Unable to create notification. Please check your entries and try again.", "error");
      return;
    }
    const snapshot: FormState = {
      ...form,
      estimatedUserCount: recipientCount ?? 0,
    };
    setSendLoading(true);
    try {
      // Call onSend and handle both sync and async scenarios
      const result = onSend(snapshot);
      if (result instanceof Promise) {
        await result;
      }
    } catch (err) {
      console.error("Error sending notification:", err);
      toast.show("Failed to send notification. Please try again.", "error");
    } finally {
      setSendLoading(false);
    }
    resetForm();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      {/* Overlay */}
      <Pressable style={modalStyles.overlay} onPress={handleClose} />

      {/* Modal card */}
      <View style={modalStyles.modalWrap} pointerEvents="box-none">
        <View style={[modalStyles.modal, isDark && { backgroundColor: "#202020" }]}>

          {/* ── Confirm-leave overlay ── */}
          {showConfirm && (
            <View style={[modalStyles.confirmOverlay, isDark && { backgroundColor: "rgba(20,20,20,0.95)" }]}>
              <View style={[modalStyles.confirmCard, isDark && { backgroundColor: "#202020" }]}>
                <MaterialIcons name="warning-amber" size={28} color="#C12C01" style={{ marginBottom: 10 }} />
                <Text style={[modalStyles.confirmTitle, isDark && { color: "#F2F2F2" }]}>Discard notification?</Text>
                <Text style={[modalStyles.confirmBody, isDark && { color: "#B0B0B0" }]}>
                  All entered information will be lost.
                </Text>
                <View style={modalStyles.confirmActions}>
                  <Pressable
                    onPress={() => setShowConfirm(false)}
                    style={({ pressed }) => [modalStyles.confirmBtnStay, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, pressed ? { opacity: 0.7 } : null]}
                  >
                    <Text style={[modalStyles.confirmBtnStayText, isDark && { color: "#F2F2F2" }]}>Stay</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleConfirmDiscard}
                    style={({ pressed }) => [modalStyles.confirmBtnDraft, { backgroundColor: "#C12C01" }, pressed ? { opacity: 0.8 } : null]}
                  >
                    <Text style={modalStyles.confirmBtnDraftText}>Discard</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          )}
          {/* Header */}
          <View style={[modalStyles.modalHeader, isDark && { borderBottomColor: "#363636" }]}>
            <View style={modalStyles.modalTitleRow}>
              <MaterialIcons name="notifications" size={20} color={isDark ? "#3D8BFF" : "#034895"} />
              <Text style={[modalStyles.modalTitle, isDark && { color: "#F2F2F2" }]}>{isEditMode ? "Edit Notification" : "Create Notification"}</Text>
            </View>
            <Pressable
              onPress={handleClose}
              style={({ pressed }) => [
                modalStyles.closeBtn,
                isDark && pressed && { backgroundColor: "#2B2B2B" },
                pressed ? modalStyles.closeBtnPressed : null,
              ]}
            >
              <MaterialIcons name="close" size={18} color={isDark ? "#B0B0B0" : "#585858"} />
            </Pressable>
          </View>

          {/* Body */}
          <ScrollView
            style={[modalStyles.modalBody, isDark && { backgroundColor: "#202020" }]}
            contentContainerStyle={modalStyles.modalBodyContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* ── Type + Priority ── */}
            <View style={modalStyles.formRow}>
              <View style={modalStyles.formCol}>
                <InlineSelect label="Notification Type" required value={form.notifType} options={NOTIF_TYPES} onChange={v => patch("notifType", v)} />
              </View>
              <View style={modalStyles.formCol}>
                <InlineSelect label="Priority" required value={form.priority} options={PRIORITIES} onChange={v => patch("priority", v)} />
              </View>
            </View>

            {/* ── Title ── */}
            <View style={modalStyles.fieldWrap}>
              <Text style={[modalStyles.label, isDark && { color: "#B0B0B0" }]}>Title <Text style={modalStyles.req}>*</Text></Text>
              <TextInput
                style={[modalStyles.textInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }, titleFocused ? modalStyles.textInputFocused : null, titleFocused ? dk?.fieldOpen : null, errors.title ? modalStyles.textInputError : null, errors.title && dk ? { backgroundColor: themeColors.statusBackground.error, borderColor: themeColors.status.error } : null]}
                placeholder="Short headline shown in notification list"
                placeholderTextColor={isDark ? "#6E6E6E" : "#a8a8a8"}
                maxLength={100}
                value={form.title}
                onChangeText={v => { patch("title", v); if (errors.title) setErrors(p => { const { title, ...r } = p; return r; }); }}
                onFocus={() => setTitleFocused(true)}
                onBlur={() => {
                  setTitleFocused(false);
                  const title = form.title ?? "";
                  const e = !title.trim() ? "Title is required." : title.length > 100 ? "Title must be 100 characters or less." : undefined;
                  setErrors(p => e ? { ...p, title: e } : (({ title, ...r }) => r)(p));
                }}
              />
              <View style={modalStyles.fieldFooter}>
                <FieldError error={errors.title} />
                <Text style={[modalStyles.charCounter, isDark && { color: "#6E6E6E" }]}>{(form.title ?? "").length}/100</Text>
              </View>
            </View>

            {/* ── Message ── */}
            <View style={modalStyles.fieldWrap}>
              <Text style={[modalStyles.label, isDark && { color: "#B0B0B0" }]}>Message <Text style={modalStyles.req}>*</Text></Text>
              <TextInput
                style={[modalStyles.textArea, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }, msgFocused ? modalStyles.textInputFocused : null, msgFocused ? dk?.fieldOpen : null, errors.message ? modalStyles.textInputError : null, errors.message && dk ? { backgroundColor: themeColors.statusBackground.error, borderColor: themeColors.status.error } : null]}
                placeholder="Full message body. Keep it concise and actionable."
                placeholderTextColor={isDark ? "#6E6E6E" : "#a8a8a8"}
                multiline
                numberOfLines={4}
                value={form.message}
                onChangeText={v => {
                  // No native maxLength here so paste overflow is visible to this handler for truncation + warning.
                  if (v.length > MESSAGE_MAX_LENGTH) {
                    const overage = v.length - MESSAGE_MAX_LENGTH;
                    patch("message", v.slice(0, MESSAGE_MAX_LENGTH));
                    // Overage > 1 means a paste/large insert pushed past the limit, not a single keystroke.
                    setMsgOverflowWarning(overage > 1);
                  } else {
                    patch("message", v);
                    setMsgOverflowWarning(false);
                  }
                  if (errors.message) setErrors(p => { const { message, ...r } = p; return r; });
                }}
                onFocus={() => setMsgFocused(true)}
                onBlur={() => {
                  setMsgFocused(false);
                  const message = form.message ?? "";
                  const e = !message.trim()
                    ? "Message is required."
                    : message.length > MESSAGE_MAX_LENGTH
                    ? `Message must be ${MESSAGE_MAX_LENGTH.toLocaleString()} characters or less.`
                    : undefined;
                  setErrors(p => e ? { ...p, message: e } : (({ message, ...r }) => r)(p));
                }}
                textAlignVertical="top"
              />
              {msgOverflowWarning ? (
                <Text style={modalStyles.overflowWarningText}>
                  Message limited to {MESSAGE_MAX_LENGTH.toLocaleString()} characters. Check the end of your message before publishing.
                </Text>
              ) : null}
              <View style={modalStyles.fieldFooter}>
                <FieldError error={errors.message} />
                <Text style={[modalStyles.charCounter, isDark && { color: "#6E6E6E" }]}>{(form.message ?? "").length.toLocaleString()}/{MESSAGE_MAX_LENGTH.toLocaleString()}</Text>
              </View>
            </View>


            {/* ── Related Ticket ── */}
            <View style={modalStyles.fieldWrap}>
              <View style={modalStyles.labelRow}>
                <Text style={[modalStyles.label, isDark && { color: "#B0B0B0" }]}>Link to Ticket</Text>
                <Text style={[modalStyles.optional, isDark && { color: "#6E6E6E" }]}>(optional)</Text>
              </View>
              <TicketSearch value={form.linkedTicket} onChange={v => patchLinkedTicket(v)} />
              <Text style={[modalStyles.helpText, isDark && { color: "#6E6E6E" }]}>Recipients can click through to the linked ticket for full context.</Text>
            </View>

            {/* Conditional ticket options */}
            <View style={[modalStyles.ticketConditionals, dk?.panel]}>
              {/* Display until ticket is Closed — only show when a ticket is linked */}
              {form.linkedTicket ? (
                <CheckRow
                  label="Display banner until ticket is Closed / Resolved"
                  checked={form.displayUntilClosed}
                  onToggle={() => {
                    const newValue = !form.displayUntilClosed;
                    patch("displayUntilClosed", newValue);
                    // Clear date-related errors when enabling displayUntilClosed since dates are not required
                    if (newValue) {
                      setErrors((e) => {
                        const { startDate, endDate, dateRange, ...rest } = e;
                        return rest;
                      });
                    }
                  }}
                />
              ) : null}
              <View style={form.linkedTicket ? { marginTop: 10 } : {}}>
                <ToggleSwitch
                  label="Include Action Button"
                  value={form.includeActionButton}
                  onToggle={() => patch("includeActionButton", !form.includeActionButton)}
                  disabled={!form.linkedTicket}
                  disabledHint="Select a related ticket to enable"
                  sublabel='Adds a “View Ticket” button to the notification'
                />
              </View>
            </View>

            {/* ── Target Audience ── */}
            <View style={[modalStyles.audienceSection, isDark && { backgroundColor: "#1a1a1a", borderWidth: 1, borderColor: "#363636" }]}>
              <Text style={[modalStyles.sectionTitle, isDark && { color: "#F2F2F2" }]}>Target Audience</Text>

              {/* ── Audience type chips (multi-select) ── */}
              <View style={[modalStyles.fieldWrap, { marginBottom: 12 }]}>
                <Text style={[modalStyles.label, isDark && { color: "#B0B0B0" }]}>
                  Audience Type <Text style={modalStyles.req}>*</Text>
                </Text>
                <View style={audLocal.chipsWrap}>
                  {AUDIENCE_TYPES.map((type) => {
                    const isSelected = (form.audienceTypes ?? []).includes(type);
                    const isDisabled =
                      type !== "All Users" && (form.audienceTypes ?? []).includes("All Users");
                    return (
                      <Pressable
                        key={type}
                        onPress={() => !isDisabled && handleAudienceTypeToggle(type)}
                        style={({ pressed }) => [
                          audLocal.chip,
                          dk?.chip,
                          isSelected && audLocal.chipSelected,
                          isSelected && dk?.chipSelected,
                          isDisabled && audLocal.chipDisabled,
                          isDisabled && dk?.chipDisabled,
                          pressed && !isDisabled && audLocal.chipPressed,
                          pressed && !isDisabled && dk?.chipPressed,
                        ]}
                      >
                        {isSelected ? (
                          <MaterialIcons name="check" size={11} color={dk ? themeColors.brand.primary : "#034895"} style={{ marginRight: 4 }} />
                        ) : null}
                        <Text
                          style={[
                            audLocal.chipText,
                            dk?.secondaryText,
                            isSelected && audLocal.chipTextSelected,
                            isSelected && dk?.brandText,
                            isDisabled && audLocal.chipTextDisabled,
                            isDisabled && dk?.chipTextDisabled,
                          ]}
                        >
                          {type}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
                {errors.audienceTypes ? (
                  <FieldError error={errors.audienceTypes} />
                ) : (form.audienceTypes ?? []).includes("All Users") ? (
                  <View style={[modalStyles.allUsersNote, dk?.infoNote, { marginTop: 8 }]}>
                    {recipientLoading ? (
                      <ActivityIndicator size="small" color={dk ? themeColors.brand.primary : "#034895"} style={{ marginRight: 6 }} />
                    ) : (
                      <MaterialIcons name="people" size={15} color={dk ? themeColors.brand.primary : "#034895"} />
                    )}
                    <Text style={[modalStyles.allUsersText, dk?.brandText]}>
                      {recipientLoading
                        ? "All Users (calculating…)"
                        : recipientCount != null && recipientCount > 0
                        ? `All ${recipientCount.toLocaleString()} users targeted. Other dimensions are overridden.`
                        : "All users targeted. Other dimensions are overridden."}
                    </Text>
                  </View>
                ) : (form.audienceTypes ?? []).length === 0 ? (
                  <Text style={[modalStyles.helpText, dk?.muted, { marginTop: 6 }]}>
                    Select one or more targeting dimensions.
                  </Text>
                ) : null}
              </View>

              {/* ── Per-dimension secondary dropdowns ── */}
              {!(form.audienceTypes ?? []).includes("All Users") &&
                (form.audienceTypes ?? []).length > 0 ? (
                <View style={audLocal.dropdownGrid}>
                  {(form.audienceTypes ?? []).includes("By Region") ? (
                    <View style={audLocal.dropdownCell}>
                      <AudienceMultiSelect
                        label="Select Regions"
                        entityName="Region"
                        values={Array.isArray(form.regions) ? form.regions : []}
                        {...dimProps("regions")}
                        onToggle={(id) => toggleArray("regions", id)}
                        helpText="OR logic within region."
                      />
                    </View>
                  ) : null}
                  {(form.audienceTypes ?? []).includes("By Market") ? (
                    <View style={audLocal.dropdownCell}>
                      <AudienceMultiSelect
                        label="Select Markets"
                        entityName="Market"
                        values={Array.isArray(form.markets) ? form.markets : []}
                        {...dimProps("markets")}
                        onToggle={(id) => toggleArray("markets", id)}
                        helpText="OR logic within market."
                      />
                    </View>
                  ) : null}
                  {(form.audienceTypes ?? []).includes("By Location") ? (
                    <View style={audLocal.dropdownCell}>
                      <AudienceMultiSelect
                        label="Select Locations"
                        entityName="Location"
                        values={Array.isArray(form.locations) ? form.locations : []}
                        {...dimProps("locations")}
                        onToggle={(id) => toggleArray("locations", id)}
                        helpText={
                          (form.locations ?? []).length > 0
                            ? `${form.locations.length} location${form.locations.length !== 1 ? "s" : ""} selected`
                            : undefined
                        }
                      />
                    </View>
                  ) : null}
                  {(form.audienceTypes ?? []).includes("By Role") ? (
                    <View style={audLocal.dropdownCell}>
                      <AudienceMultiSelect
                        label="Select Roles"
                        entityName="Role"
                        values={Array.isArray(form.roles) ? form.roles : []}
                        {...dimProps("roles")}
                        onToggle={(id) => toggleArray("roles", id)}
                        helpText="OR logic within role."
                      />
                    </View>
                  ) : null}
                  {(form.audienceTypes ?? []).includes("By Target System") ? (
                    <View style={audLocal.dropdownCell}>
                      <AudienceMultiSelect
                        label="Target Systems"
                        entityName="System"
                        values={Array.isArray(form.targetSystems) ? form.targetSystems : []}
                        {...dimProps("systems")}
                        onToggle={(id) => toggleArray("targetSystems", id)}
                        helpText="Targets users whose assigned locations use the selected systems."
                      />
                    </View>
                  ) : null}
                </View>
              ) : null}

              {/* Send Via + Dates */}
              <View style={modalStyles.formRow}>
                <View style={modalStyles.formCol}>
                  <View style={modalStyles.fieldWrap}>
                    <Text style={[modalStyles.label, dk?.label]}>Send Via</Text>
                    <View style={modalStyles.checkGroup}>
                      <CheckRow label="In-App Banner" checked={form.sendVia.inApp}  onToggle={() => patch("sendVia", { ...form.sendVia, inApp:  !form.sendVia.inApp  })} />
                      <CheckRow label="Email"         checked={form.sendVia.email} onToggle={() => patch("sendVia", { ...form.sendVia, email: !form.sendVia.email })} />
                      <CheckRow label="Teams"         checked={form.sendVia.teams} onToggle={() => patch("sendVia", { ...form.sendVia, teams: !form.sendVia.teams })} />
                    </View>
                  </View>
                </View>

                <View style={modalStyles.formCol}>
                  <View style={modalStyles.fieldWrap}>
                    <Text style={[modalStyles.label, dk?.label]}>
                      Start / End
                      {!form.displayUntilClosed ? <Text style={modalStyles.req}> *</Text> : null}
                    </Text>
                    {form.displayUntilClosed ? (
                      <View style={[modalStyles.dateAutoNote, dk?.panel]}>
                        <MaterialIcons name="info-outline" size={13} color={dk ? themeColors.text.secondary : "#818181"} />
                        <Text style={[modalStyles.dateAutoText, dk?.secondaryText]}>Banner displays until the linked ticket is closed.</Text>
                      </View>
                    ) : (
                      <>
                        <View style={modalStyles.dateRow}>
                          <View style={{ flex: 1 }}>
                            <DateTimeField label="" value={form.startDate} onChange={v => patchDateWithValidation("startDate", v)} />
                          </View>
                          <View style={{ flex: 1 }}>
                            <DateTimeField label="" value={form.endDate}   onChange={v => patchDateWithValidation("endDate", v)} />
                          </View>
                        </View>
                        {(errors.dateRange || errors.startDate || errors.endDate) && (
                          <FieldError error={errors.dateRange ?? errors.startDate ?? errors.endDate} />
                        )}
                      </>
                    )}
                  </View>
                </View>
              </View>

              {/* Targeting preview — shown after Send Via */}
              <View style={modalStyles.recipientRow}>
                {recipientLoading ? (
                  <ActivityIndicator size="small" color={dk ? themeColors.brand.primary : "#034895"} style={{ marginRight: 6 }} />
                ) : (
                  <MaterialIcons
                    name="people"
                    size={14}
                    color={
                      (form.audienceTypes ?? []).includes("All Users") || (recipientCount != null && recipientCount > 0)
                        ? (dk ? themeColors.brand.primary : "#034895")
                        : (dk ? themeColors.text.secondary : "#818181")
                    }
                  />
                )}
                <Text
                  style={[
                    modalStyles.recipientText,
                    dk?.brandText,
                    !recipientLoading &&
                    !(form.audienceTypes ?? []).includes("All Users") &&
                    (recipientCount == null || recipientCount === 0)
                      ? { color: dk ? themeColors.text.secondary : "#818181" }
                      : null,
                  ]}
                >
                  {recipientLoading
                    ? "Calculating reach…"
                    : recipientCount != null && recipientCount > 0
                    ? `This notification will reach approximately ${recipientCount.toLocaleString()} users.`
                    : "Select a target audience to see estimated reach."}
                </Text>
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={[modalStyles.modalFooter, isDark && { borderTopColor: "#363636" }]}>
            <Pressable
              onPress={handleClose}
              style={({ pressed }) => [
                modalStyles.footerBtnGhost,
                pressed ? modalStyles.footerBtnGhostPressed : null,
                pressed && dk ? { backgroundColor: themeColors.background.elevated } : null,
              ]}
            >
              <Text style={[modalStyles.footerBtnGhostText, isDark && { color: "#B0B0B0" }]}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={handleDraft}
              style={({ pressed }) => [
                modalStyles.footerBtnSecondary,
                isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" },
                pressed ? modalStyles.footerBtnSecondaryPressed : null,
                pressed && dk ? { backgroundColor: themeColors.border.divider } : null,
              ]}
            >
              <Text style={[modalStyles.footerBtnSecondaryText, isDark && { color: "#3D8BFF" }]}>Save as Draft</Text>
            </Pressable>
            <Pressable
              onPress={handleSend}
              disabled={sendLoading}
              style={({ pressed }) => [
                modalStyles.footerBtnPrimary,
                isDark && { backgroundColor: colors?.brand?.primary ?? "#3D8BFF" },
                pressed && !sendLoading ? modalStyles.footerBtnPrimaryPressed : null,
                sendLoading ? { opacity: 0.6 } : null,
              ]}
            >
              {sendLoading ? (
                <ActivityIndicator size="small" color="#ffffff" style={{ marginRight: 4 }} />
              ) : (
                <MaterialIcons name={isEditMode ? "save" : "send"} size={14} color="#ffffff" />
              )}
              <Text style={modalStyles.footerBtnPrimaryText}>
                {sendLoading ? "Sending..." : isEditMode ? "Save Changes" : "Send Notification"}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/* ── Popup overlay dropdown styles (shared by InlineSelect, MultiSelect, AudienceMultiSelect) ── */
const popupStyles = StyleSheet.create({
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  dropdown: {
    position: "absolute",
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    paddingVertical: 4,
    shadowColor: "#001E46",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 16,
    zIndex: 9999,
  },
});

/* ── Audience chip & dropdown grid styles ────────────────────────────────── */
const audLocal = StyleSheet.create({
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 6,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 100,
    borderWidth: 1.5,
    borderColor: "#D0D0D0",
    backgroundColor: "#fafafa",
  },
  chipSelected: {
    borderColor: "#034895",
    backgroundColor: "#EFF6FF",
  },
  chipDisabled: {
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    opacity: 0.55,
  },
  chipPressed: { backgroundColor: "#DBEAFE" },
  chipText: { fontSize: 13, color: "#585858", fontWeight: "500" },
  chipTextSelected: { color: "#034895", fontWeight: "600" },
  chipTextDisabled: { color: "#9CA3AF" },

  // Grid of secondary dropdowns — two columns on wide screens, wraps on small
  dropdownGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 4,
    marginBottom: 8,
  },
  dropdownCell: { flex: 1, minWidth: 220 },
});

export default CreateNotificationModal;
